import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Space, Tag, Spin, Alert, Button, Select, Progress, Tooltip, message } from 'antd';
import {
  SaveOutlined, CloseOutlined,
  TranslationOutlined, StopOutlined, PauseOutlined, PlayCircleOutlined,
} from '@ant-design/icons';
import { withLocale } from '../../l10n';
import DataTable from '../DataTable/DataTable';
import TranslationCell from './TranslationCell';
import "./TranslateLibrary.scss";
import { GetColumnLiteralSearchProps } from '../../utils/functions.jsx';
import { L10N_LIST, getLanguageName } from '../../utils/l10n.constants';
import {
  useGetLibraryQuery,
  useGetLibraryParamsQuery,
  useGetContextsQuery,
  useUpdateLibraryParamsMutation,
  useTranslateOneMutation,
} from '../../services/translateApi';
import {
  setContextId, setTranslation,
  loadDraft, resetDraft, markDraftSaved,
  bulkStart, applyTranslations, bulkError,
  bulkStopping, bulkPausing, bulkPaused, bulkResume, bulkFinish,
} from '../../store';
import {
  validateTranslate,
  validateTranslateDefaultLanguage,
} from '../../utils/translateValidation';

/** Cada cuánto se vuelca el buffer de resultados al store. */
const FLUSH_MS = 1500;

function TranslateLibrary({ lang }) {

  // ===== 1. ESTADOS =====
  const { libraryId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const store = useStore();

  // Parámetros con errores al pulsar Validar. null = sin filtrar.
  const [invalidIds, setInvalidIds] = useState(null);
  // Celda que se está traduciendo, como 'id|idioma', para su spinner.
  const [translating, setTranslating] = useState(null);
  // Filas marcadas para «traducir seleccionadas».
  const [selectedIds, setSelectedIds] = useState([]);

  // Cuando tenemos algo sin guardar, se deriva de `dirtyKeys` en vez de guardarse en el slice
  const dirty = useSelector((state) => Object.keys(state.translate.dirtyKeys).length > 0);
  const contextId = useSelector((state) => state.translate.contextId);
  const bulk = useSelector((state) => state.translate.bulk);
  
  // Mientras esta corriendo, se bloquean las celdas, mientras esta en pausa permite editar. 
  const busy = ['running', 'stopping', 'pausing'].includes(bulk.state);
  const paused = bulk.state === 'paused';


  const workerRef = useRef(null);
  const bufferRef = useRef([]);
  const flushRef = useRef(null);
  const jobsRef = useRef([]); // Lista de las traducciones pendientes
  const cursorRef = useRef(0); // Marca cuantas de las jobsRef ya se han procesado.
  const intentRef = useRef(null); // Qué pidió el usuario al cortar: 'pause' o 'cancel'. Se lee al llegar 'done'.
  const topRef = useRef(null);  // Cabecera de la pantalla: al filtrar por errores se sube hasta aquí

  // ===== 2. DATOS =====
  const { currentData: library } = useGetLibraryQuery(libraryId, { skip: !libraryId });
  const { currentData: params, isFetching, isError } = useGetLibraryParamsQuery(libraryId, { skip: !libraryId });
  const [updateParams, { isLoading: isSaving }] = useUpdateLibraryParamsMutation();
  
  const { data: contexts = [] } = useGetContextsQuery();
  const [translateOne] = useTranslateOneMutation();

  const defaultLang = library?.l10n?.default_lang;

  // ===== 3. EFECTOS =====

  // Cleanup al cambiar de librería **y al desmontar**. Cubre dos situaciones con un solo effect
  useEffect(() => {
    setInvalidIds(null);
    setSelectedIds([]);
    return () => {
      killWorker();
      dispatch(resetDraft());
    };
  }, [libraryId, dispatch]);

  // Genera borrador de parametros (original y copia) para gestionar ediciones sobre borrador
  useEffect(() => {
    if (params) dispatch(loadDraft(params));
  }, [params, dispatch]);

  // ===== 4. LECTURAS DEL BORRADOR (bajo demanda) =====
  const readDraft = () => store.getState().translate.draft;

  /** Encuentra las celdas no válidas en todo el borrador. Se llama al guardar */
  const findInvalid = () => {
    const { draft } = store.getState().translate;
    const out = [];

    for (const id of draft.ids) {
      const translations = draft.byId[id]?.l10n?.translations ?? {};
      for (const [code, tr] of Object.entries(translations)) {
        const value = String(tr.description ?? '');
        const check = code === defaultLang
          ? validateTranslateDefaultLanguage(value)
          : validateTranslate(value);
        if (!check.isValid) out.push({ id, code });
      }
    }
    return out;
  };

  /** Serializa el borrador en el formato que espera el PUT */
  const buildPayload = () => {
    const draft = readDraft();
    return draft.ids.map((id) => draft.byId[id]);
  };

  // ===== 5. HANDLERS =====

  /**
   * Traduce una celda con IA. El texto origen se lee del BORRADOR
   * El resultado queda validado y marcado como cambio pendiente de guardar
   */
  const translateCell = async (record, code) => {
    const draft = readDraft();
    const source = String(
      draft.byId[record.id]?.l10n?.translations?.[defaultLang]?.description ?? ''
    );

    if (!source) {
      message.warning(lang.handle('El texto en el idioma origen está vacío'));
      return;
    }

    setTranslating(`${record.id}|${code}`);
    try {
      const res = await translateOne({
        id: record.id,
        language_from: defaultLang,
        language_to: code,
        context: contextId,
        value: source,
      }).unwrap();

      dispatch(setTranslation({ id: res.id, code: res.language, value: res.value }));
      message.success(`${record.name} → ${lang.handle(getLanguageName(code))}`);
    } catch (err) {
      console.error('Error al traducir:', err);
      message.error(lang.handle('No se ha podido traducir'));
    } finally {
      setTranslating(null);
    }
  };

  /**
   * Qué hay que traducir: por cada parámetro con texto en el idioma origen,
   * las celdas de los demás idiomas que estén vacías.
   * Con `onlyIds` se limita a las filas seleccionadas.
   */
  const buildJobs = (onlyIds = null) => {
    const draft = readDraft();
    const wanted = onlyIds ? new Set(onlyIds) : null;
    const jobs = [];

    for (const id of draft.ids) {
      if (wanted && !wanted.has(id)) continue;

      const translations = draft.byId[id]?.l10n?.translations ?? {};
      const source = String(translations[defaultLang]?.description ?? '');
      if (!source) continue;

      for (const code of L10N_LIST) {
        if (code === defaultLang) continue;
        if (String(translations[code]?.description ?? '')) continue;
        jobs.push({ id, code, value: source });
      }
    }

    return jobs;
  };

  const flushBuffer = () => {
    if (bufferRef.current.length === 0) return;
    const batch = bufferRef.current;
    bufferRef.current = [];
    dispatch(applyTranslations(batch));
  };


  // WORKER MANAGEMENT //

  /** Cierra el intervalo, vuelca lo que quede y mata el worker. */
  const stopWorker = () => {
    if (flushRef.current) {
      clearInterval(flushRef.current);
      flushRef.current = null;
    }
    flushBuffer();
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
  };

  /**
   * Apagado al abandonar la librería, **sin volcar el buffer**
   * Limpia además `jobsRef` y `cursorRef`
   */
  const killWorker = () => {
    if (flushRef.current) {
      clearInterval(flushRef.current);
      flushRef.current = null;
    }
    bufferRef.current = [];
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
    jobsRef.current = [];
    cursorRef.current = 0;
    intentRef.current = null;
  };

  /** Lanza un worker nuevo con la lista dada. Cada arranque es una conexión nueva. */
  const runWorker = (jobs) => {
    jobsRef.current = jobs;
    cursorRef.current = 0;
    intentRef.current = null;

    const worker = new Worker(
      new URL('../../workers/translateWorker.js', import.meta.url),
      { type: 'module' }
    );
    workerRef.current = worker;

    worker.onmessage = (e) => {
      const msg = e.data ?? {};

      if (msg.type === 'result') {
        bufferRef.current.push({ id: msg.id, code: msg.code, value: msg.value });
        return;
      }

      if (msg.type === 'reconnecting') {
        message.warning(
          `${lang.handle('Conexión perdida, reintentando')} (${msg.attempt}/${msg.max})`
        );
        return;
      }

      if (msg.type === 'error') {
        console.error('Traducción fallida:', msg);
        dispatch(bulkError());
        return;
      }

      if (msg.type === 'done') {
        cursorRef.current += msg.processed ?? 0;
        stopWorker();

        const wasPause = msg.cancelled && intentRef.current === 'pause';
        intentRef.current = null;

        if (wasPause) {
          dispatch(bulkPaused());
          message.info(lang.handle('Traducción en pausa'));
          return;
        }

        jobsRef.current = [];
        cursorRef.current = 0;
        dispatch(bulkFinish());

        const resumen = `${msg.translated}/${msg.total}`;
        if (msg.aborted) {
          message.warning(`${lang.handle('Traducción interrumpida: se ha perdido la conexión')} (${resumen})`);
        } else if (msg.cancelled) {
          message.info(`${lang.handle('Traducción cancelada')} (${resumen})`);
        } else {
          message.success(`${lang.handle('Traducción terminada')} (${resumen})`);
        }
      }
    };

    worker.onerror = (err) => {
      console.error('Error en el worker de traducción:', err);
      message.error(lang.handle('Error en el proceso de traducción'));
      stopWorker();
      jobsRef.current = [];
      cursorRef.current = 0;
      dispatch(bulkFinish());
    };

    worker.postMessage({
      command: 'start',
      wsUrl: import.meta.env.VITE_TRANSLATE_WS_URL,
      jobs,
      languageFrom: defaultLang,
      context: contextId,
      timeoutMs: 30000,
    });

    flushRef.current = setInterval(flushBuffer, FLUSH_MS);
  };


  // BULK CONTROL //

  /** Requisitos comunes a cualquier arranque. */
  const bulkReady = () => {
    if (!contextId || !defaultLang) {
      message.warning(lang.handle('Elija un contexto para poder traducir'));
      return false;
    }
    return true;
  };

  /** Arranca desde cero, sobre toda la librería o sobre las filas marcadas. */
  const startBulk = (onlyIds = null) => {
    if (!bulkReady()) return;

    const jobs = buildJobs(onlyIds);
    if (jobs.length === 0) {
      message.info(lang.handle('No hay traducciones pendientes'));
      return;
    }

    dispatch(bulkStart(jobs.length));
    runWorker(jobs);
  };

  /** Pausa el proceso en curso, con posibilidad de reanudación. Se permite editar campos durante la pausa */
  const pauseBulk = () => {
    intentRef.current = 'pause';
    dispatch(bulkPausing());
    if (workerRef.current) workerRef.current.postMessage({ command: 'cancel' });
  };

  /**
   * Continúa donde se quedó. Recalcula lo que queda contra el borrador actual
   */
  const resumeBulk = () => {
    if (!bulkReady()) return;

    const draft = readDraft();
    const pending = jobsRef.current
      .slice(cursorRef.current)
      .filter((job) => !String(draft.byId[job.id]?.l10n?.translations?.[job.code]?.description ?? ''))
      .map((job) => ({
        ...job,
        value: String(
          draft.byId[job.id]?.l10n?.translations?.[defaultLang]?.description ?? ''
        ),
      }))
      .filter((job) => job.value);

    if (pending.length === 0) {
      jobsRef.current = [];
      cursorRef.current = 0;
      dispatch(bulkFinish());
      message.info(lang.handle('No quedan traducciones pendientes'));
      return;
    }

    dispatch(bulkResume({ total: bulk.done + pending.length }));
    runWorker(pending);
  };

  /** Cancela el proceso en curso, sin posibilidad de reanudación. */
  const cancelBulk = () => {
    if (paused) {
      jobsRef.current = [];
      cursorRef.current = 0;
      dispatch(bulkFinish());
      message.info(lang.handle('Traducción cancelada'));
      return;
    }

    intentRef.current = 'cancel';
    dispatch(bulkStopping());
    if (workerRef.current) workerRef.current.postMessage({ command: 'cancel' });
  };


  // SAVING //
  const save = async ({ close = false } = {}) => {
    const invalid = findInvalid();
    //Si se detectan fallos, se filtra a las filas culpables y se avisa
    if (invalid.length > 0) {
      setInvalidIds(new Set(invalid.map((item) => item.id)));
      topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      message.error(lang.handle('Corrija las traducciones no válidas antes de guardar'));
      return;
    }

    try {
      await updateParams({ uuid: libraryId, parameters: buildPayload() }).unwrap();
      dispatch(markDraftSaved());
      message.success(lang.handle('Traducciones guardadas'));
      if (close) {
        navigate('/translator');
      }
    } catch (err) {
      console.error('Error al guardar las traducciones:', err);
      message.error(lang.handle(
        err?.upstream === 403
          ? 'No tiene permiso para modificar esta librería' // Error rescatado de Cloud
          : 'No se han podido guardar las traducciones'
      ));
    }
  };

  const close = () => navigate('/translator');

  // ===== 8. CONFIG (columnas) =====
  const columns = [
    {
      title: lang.handle('Parámetro'),
      dataIndex: 'name',
      fixed: 'left',
      width: 200,
      sorter: (a, b) => String(a.name || '').localeCompare(String(b.name || '')),
      ...GetColumnLiteralSearchProps('name', lang)
    },
    ...L10N_LIST.map((code) => ({
      title: (
        <Space size={4}>
          {lang.handle(getLanguageName(code))}
          {code === defaultLang && <Tag color="blue">{lang.handle('origen')}</Tag>}
        </Space>
      ),
      key: code,
      width: 260,
      render: (_value, record) => (
        <TranslationCell
          id={record.id}
          code={code}
          isDefault={code === defaultLang}
          hasContext={!!contextId && !!defaultLang}
          disabled={busy}
          translating={translating === `${record.id}|${code}`}
          onTranslate={() => translateCell(record, code)}
        />
      ),
    })),
  ];

  // ===== 9. RENDER =====
  if (isError) {
    return (
      <Alert
        type="error"
        showIcon
        message={lang.handle('No se han podido cargar los parámetros de la librería')}
      />
    );
  }

  if (isFetching && !params) {
    return (
      <div className="translate-library__loading">
        <Spin size="large" />
      </div>
    );
  }

  const rows = params ?? [];
  const visibleRows = invalidIds ? rows.filter((p) => invalidIds.has(p.id)) : rows;

  return (
    <div className="translate-library">
      <div className="translate-library__header" ref={topRef}>
        <Space>
          <span className="translate-library__name">{library?.name}</span>
          <Tag>{library?.parent ? lang.handle('plantilla') : lang.handle('librería')}</Tag>
          {defaultLang && <Tag color="blue">{lang.handle(getLanguageName(defaultLang))}</Tag>}
          {dirty && <Tag color="orange">{lang.handle('cambios sin guardar')}</Tag>}
        </Space>

        <Tooltip title={lang.handle('Cerrar')}>
          <Button
            type="text"
            shape="circle"
            icon={<CloseOutlined />}
            disabled={busy}
            onClick={close}
          />
        </Tooltip>
      </div>

      {invalidIds &&
        <div className="translate-library__filters">
          <span className="translate-library__filters-label">
            {lang.handle('Filtros aplicados')}:
          </span>
          <Tag color="red" closable onClose={() => setInvalidIds(null)}>
            {lang.handle('sólo no válidas')} ({invalidIds.size})
          </Tag>
        </div>
      }

      <Space className="translate-library__context">
        <Select
          className="translate-library__context-select"
          placeholder={lang.handle('Contexto de traducción')}
          value={contextId ?? undefined}
          onChange={(value) => dispatch(setContextId(value))}
          options={contexts.map((c) => ({ value: c.id, label: c.name }))}
        />
        {!contextId &&
          <span className="translate-library__hint">
            {lang.handle('Elija un contexto para poder traducir')}
          </span>
        }
      </Space>

      {/* BARRA DE PROGRESO Y CONTROLES */}
      {bulk.state !== 'idle' &&
        <div className="translate-library__run">
          <Space size={0}>
            {paused ? (
              <Tooltip title={lang.handle('Continuar')}>
                <Button
                  type="text"
                  shape="circle"
                  icon={<PlayCircleOutlined />}
                  onClick={resumeBulk}
                />
              </Tooltip>
            ) : (
              <Tooltip title={lang.handle('Pausar')}>
                <Button
                  type="text"
                  shape="circle"
                  icon={<PauseOutlined />}
                  disabled={bulk.state !== 'running'}
                  onClick={pauseBulk}
                />
              </Tooltip>
            )}
            <Tooltip title={lang.handle('Cancelar')}>
              <Button
                type="text"
                shape="circle"
                danger
                icon={<StopOutlined />}
                disabled={bulk.state === 'stopping'}
                onClick={cancelBulk}
              />
            </Tooltip>
          </Space>

          <Progress
            className="translate-library__run-bar"
            percent={bulk.total ? Math.round((bulk.done / bulk.total) * 100) : 0}
            status={
              bulk.state === 'stopping' ? 'exception'
                : paused ? 'normal'
                  : 'active'
            }
            showInfo={false}
          />

          <span className="translate-library__hint">
            {`${bulk.done}/${bulk.total}`}
            {bulk.errors > 0 && ` · ${bulk.errors} ${lang.handle('con error')}`}
            {bulk.state === 'stopping' && ` · ${lang.handle('cancelando…')}`}
            {bulk.state === 'pausing' && ` · ${lang.handle('pausando…')}`}
            {paused && ` · ${lang.handle('en pausa')}`}
          </span>
        </div>
      }

      {/* TABLA */}
      <DataTable
        key={invalidIds ? 'invalid' : 'all'}
        rowSelection={{ selectedRowKeys: selectedIds, onChange: setSelectedIds }}
        columns={columns}
        dataSource={visibleRows}
        rowKey="id"
        loading={isFetching}
        scroll={{ x: 'max-content' }}
      />

      <div className="button-panel">
        <Space>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={isSaving}
            disabled={busy}
            onClick={() => save()}
          >
            {lang.handle('Guardar')}
          </Button>
          <Button
            icon={<SaveOutlined />}
            loading={isSaving}
            disabled={busy}
            onClick={() => save({ close: true })}
          >
            {lang.handle('Guardar y cerrar')}
          </Button>
          <Button
            icon={<TranslationOutlined />}
            disabled={busy || paused || !contextId}
            onClick={() => startBulk()}
          >
            {lang.handle('Traducir todo')}
          </Button>
          {selectedIds.length > 0 &&
            <Button
              icon={<TranslationOutlined />}
              disabled={busy || paused || !contextId}
              onClick={() => startBulk(selectedIds)}
            >
              {`${lang.handle('Traducir seleccionadas')} (${selectedIds.length})`}
            </Button>
          }
        </Space>
      </div>
    </div>
  );
}

export default withLocale(TranslateLibrary);
